export const FRONT_CHAT_SCRIPT_URL = "https://chat-assets.frontapp.com/v1/chat.bundle.js";

type FrontChatCommand = "hide" | "identity" | "init" | "show" | "shutdown";

type FrontChatApi = (command: FrontChatCommand, options?: Record<string, unknown>) => unknown;

type FrontChatWindow = typeof window & {
  FrontChat?: FrontChatApi;
  __supplyGuardFrontChat?: {
    chatId: string | null;
    identityKey?: string | null;
    isReady?: boolean;
    readyPromise?: Promise<void>;
    scriptPromise?: Promise<void>;
  };
};

const FRONT_CHAT_SCRIPT_ATTRIBUTE = "data-supply-guard-front-chat";

type FrontChatIdentity = {
  email?: string | null;
  userHash?: string | null;
  name?: string | null;
  organizationName?: string | null;
  role?: string | null;
};

function getFrontChatWindow(): FrontChatWindow | null {
  return typeof window === "undefined" ? null : (window as FrontChatWindow);
}

function getFrontChatState(frontChatWindow: FrontChatWindow) {
  frontChatWindow.__supplyGuardFrontChat ??= { chatId: null };
  return frontChatWindow.__supplyGuardFrontChat;
}

export function getConfiguredFrontChatId(): string | null {
  const chatId = import.meta.env.VITE_FRONT_CHAT_ID?.trim();
  return chatId || null;
}

function getFrontChatCustomFields(identity: FrontChatIdentity): Record<string, string> {
  const customFields: Record<string, string> = {};
  if (identity.organizationName?.trim()) {
    customFields.Organization = identity.organizationName.trim();
  }
  if (identity.role?.trim()) {
    customFields.Role = identity.role.trim();
  }
  return customFields;
}

function getFrontChatIdentityOptions(identity?: FrontChatIdentity): Record<string, unknown> {
  if (!identity) {
    return {};
  }

  const identityOptions: Record<string, unknown> = {};
  const email = identity.email?.trim();
  const userHash = identity.userHash?.trim();
  const name = identity.name?.trim();
  const customFields = getFrontChatCustomFields(identity);

  if (!email || !userHash) {
    return identityOptions;
  }

  if (email) {
    identityOptions.email = email;
  }
  identityOptions.userHash = userHash;
  if (name) {
    identityOptions.name = name;
  }
  if (Object.keys(customFields).length > 0) {
    identityOptions.customFields = customFields;
  }

  return identityOptions;
}

function loadFrontChatScript(frontChatWindow: FrontChatWindow): Promise<void> {
  if (frontChatWindow.FrontChat) {
    return Promise.resolve();
  }

  const state = getFrontChatState(frontChatWindow);
  if (state.scriptPromise) {
    return state.scriptPromise;
  }

  state.scriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[${FRONT_CHAT_SCRIPT_ATTRIBUTE}]`);

    const script = existingScript ?? document.createElement("script");
    script.src = FRONT_CHAT_SCRIPT_URL;
    script.async = true;
    script.setAttribute(FRONT_CHAT_SCRIPT_ATTRIBUTE, "true");

    const handleLoad = () => {
      if (!frontChatWindow.FrontChat) {
        reject(new Error("Front Chat SDK loaded without exposing window.FrontChat."));
        return;
      }
      resolve();
    };

    const handleError = () => {
      reject(new Error("Front Chat SDK failed to load."));
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existingScript) {
      document.head.appendChild(script);
    }
  });

  return state.scriptPromise;
}

export async function initFrontChat(
  chatId: string,
  options: { identity?: FrontChatIdentity; identityKey?: string | null; signal?: AbortSignal } = {},
): Promise<void> {
  const normalizedChatId = chatId.trim();
  if (!normalizedChatId) {
    throw new Error("Front Chat requires VITE_FRONT_CHAT_ID to be set.");
  }
  if (options.signal?.aborted) {
    return;
  }

  const frontChatWindow = getFrontChatWindow();
  if (!frontChatWindow) {
    return;
  }

  await loadFrontChatScript(frontChatWindow);
  if (options.signal?.aborted) {
    return;
  }

  const state = getFrontChatState(frontChatWindow);
  const normalizedIdentityKey = options.identityKey?.trim() || null;
  if (state.chatId === normalizedChatId && state.identityKey === normalizedIdentityKey) {
    await state.readyPromise;
    return;
  }

  if (state.chatId) {
    if (state.identityKey && state.identityKey !== normalizedIdentityKey) {
      frontChatWindow.FrontChat?.("shutdown", { clearSession: true });
    } else {
      frontChatWindow.FrontChat?.("shutdown");
    }
  }

  state.chatId = normalizedChatId;
  state.identityKey = normalizedIdentityKey;
  state.isReady = false;
  let resolveReady: () => void = () => {};
  const readyPromise = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  state.readyPromise = readyPromise;
  try {
    frontChatWindow.FrontChat?.("init", {
      chatId: normalizedChatId,
      useDefaultLauncher: false,
      ...getFrontChatIdentityOptions(options.identity),
      onInitCompleted: () => {
        if (state.chatId !== normalizedChatId || state.readyPromise !== readyPromise) {
          resolveReady();
          return;
        }
        state.isReady = true;
        resolveReady();
      },
    });
  } catch (error) {
    if (state.chatId === normalizedChatId && state.readyPromise === readyPromise) {
      state.chatId = null;
      state.identityKey = null;
      state.isReady = false;
      state.readyPromise = undefined;
    }
    resolveReady();
    throw error;
  }

  await state.readyPromise;
}

function isFrontChatReady(frontChatWindow: FrontChatWindow | null): frontChatWindow is FrontChatWindow & {
  FrontChat: FrontChatApi;
} {
  const state = frontChatWindow ? getFrontChatState(frontChatWindow) : null;
  return Boolean(frontChatWindow?.FrontChat && state?.chatId && state.isReady);
}

export function identifyFrontChatUser(identity: FrontChatIdentity): void {
  const frontChatWindow = getFrontChatWindow();
  if (!isFrontChatReady(frontChatWindow)) {
    return;
  }

  frontChatWindow.FrontChat("identity", {
    name: identity.name?.trim() || undefined,
    customFields: getFrontChatCustomFields(identity),
  });
}

export function setFrontChatVisibility(visible: boolean): void {
  const frontChatWindow = getFrontChatWindow();
  if (!isFrontChatReady(frontChatWindow)) {
    return;
  }

  frontChatWindow.FrontChat(visible ? "show" : "hide");
}

export function showFrontChatWindow(): void {
  const frontChatWindow = getFrontChatWindow();
  if (!isFrontChatReady(frontChatWindow)) {
    return;
  }

  frontChatWindow.FrontChat("show");
}

export function shutdownFrontChat(options?: { clearSession?: boolean }): void {
  const frontChatWindow = getFrontChatWindow();
  if (!frontChatWindow?.FrontChat) {
    return;
  }

  if (options) {
    frontChatWindow.FrontChat("shutdown", options);
  } else {
    frontChatWindow.FrontChat("shutdown");
  }

  const state = getFrontChatState(frontChatWindow);
  state.chatId = null;
  state.identityKey = null;
  state.isReady = false;
  state.readyPromise = undefined;
}
